import React, { useEffect, useState } from 'react';
import ahoy from 'ahoy.js';
import AppLayout from '../Layout/AppLayout';
import QboConnectionCard from './QboConnectionCard';
import ExpenseMappingTable from './ExpenseMappingTable';
import SyncStatusBanner from './SyncStatusBanner';
import useQboAccounts from '../../../hooks/useQboAccounts';
import { triggerQboSync } from './qboActions';

// SucceedoIntegrations — composition only. Connection state, accounts/mappings
// fetch + save, sync trigger, and table rendering live in extracted
// children/hooks; this file is layout + status messages.
export default function SucceedoIntegrations({
  connected: initialConnected, lastSyncedAt, connectUrl, disconnectUrl, syncUrl,
  expenseAccountsUrl, expenseMappingsUrl, qboConnected, qboError,
}) {
  const [connected] = useState(initialConnected);
  const [savingMappings, setSavingMappings] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(qboError || null);
  const [successMsg, setSuccessMsg] = useState(
    qboConnected ? 'QuickBooks connected successfully! Your data will sync shortly.' : null,
  );
  const { accounts, mappings, loading: loadingAccounts, updateMapping, saveMappings } = useQboAccounts({
    connected, expenseAccountsUrl, expenseMappingsUrl,
  });

  useEffect(() => { ahoy.track('succeedo_integrations_viewed', { connected }); }, [connected]);

  const handleSyncNow = async () => {
    setSyncing(true); setErrorMsg(null); setSuccessMsg(null);
    const r = await triggerQboSync(syncUrl);
    if (r.ok) { setSuccessMsg('Sync started — your P&L data will be updated shortly.'); ahoy.track('succeedo_qbo_sync_clicked'); }
    else if (r.error) setErrorMsg(r.error);
    setSyncing(false);
  };

  const handleSaveMappings = async () => {
    setSavingMappings(true); setSaveSuccess(false); setErrorMsg(null);
    const r = await saveMappings();
    if (r.ok) {
      setSaveSuccess(true);
      ahoy.track('succeedo_qbo_expense_mappings_saved', { mapped_count: r.mappedCount });
      setTimeout(() => setSaveSuccess(false), 3000);
    } else { setErrorMsg(r.error); }
    setSavingMappings(false);
  };

  const formattedSyncDate = lastSyncedAt
    ? new Date(lastSyncedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <AppLayout breadcrumb="INTEGRATIONS">
      <div className="p-6 max-w-[760px] mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }} />
            <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">Integrations</span>
          </div>
          <h1 className="font-serif font-light text-[32px] text-foreground tracking-[-0.02em]">Connect QuickBooks</h1>
          <p className="text-[15px] text-muted-foreground mt-1">
            Link your QuickBooks Online account to automatically pre-fill your Valuation Studio with revenue and expense data.
          </p>
        </div>

        <SyncStatusBanner successMsg={successMsg} errorMsg={errorMsg} />

        <QboConnectionCard connected={connected} formattedSyncDate={formattedSyncDate} syncing={syncing}
          syncUrl={syncUrl} connectUrl={connectUrl} disconnectUrl={disconnectUrl} onSyncNow={handleSyncNow} />

        {connected && (
          <ExpenseMappingTable accounts={accounts} mappings={mappings} loadingAccounts={loadingAccounts}
            savingMappings={savingMappings} saveSuccess={saveSuccess}
            onChangeMapping={updateMapping} onSave={handleSaveMappings} />
        )}

        <div className="mt-6 text-center">
          <a href="/succeedo/valuation" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">
            &larr; Back to Valuation Studio
          </a>
        </div>
      </div>
    </AppLayout>
  );
}
